import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

function UserProfile() {
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirm: ''
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get('/api/users/me');
      setUser(response.data);
      setFormData({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        email: response.data.email || '',
        password: '',
        password_confirm: ''
      });
      setLoading(false);
    } catch (err) {
      setError('Fehler beim Laden des Profils: ' + (err.response?.data?.detail || err.message));
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    // Password validation
    if (formData.password && formData.password !== formData.password_confirm) {
      alert(t('userProfile.passwordMismatch'));
      return;
    }

    try {
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email
      };
      
      // Nur Passwort senden wenn es geändert wurde
      if (formData.password) {
        updateData.password = formData.password;
      }

      await axios.put(`/api/users/${user.id}`, updateData);

      setEditing(false);
      fetchUserProfile();
      setFormData({ ...formData, password: '', password_confirm: '' });
      alert(t('userProfile.saveSuccess'));
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getRoleName = (role) => t(`role.${role}`) || role;

  const getRoleColor = (role) => {
    if (role === 'full_admin') return { bg: 'rgba(239, 68, 68, 0.2)', text: '#fca5a5' };
    if (role === 'tournament_admin') return { bg: 'rgba(59, 130, 246, 0.2)', text: '#93c5fd' };
    return { bg: 'rgba(148, 163, 184, 0.2)', text: '#495057' };
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '40px' }}>{t('userProfile.loading')}</div>;
  if (error) return <div style={{ color: '#f87171', textAlign: 'center', padding: '40px' }}>{error}</div>;
  if (!user) return null;

  const roleColors = getRoleColor(user.role);

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '30px' }}>
        👤 {t('userProfile.title')}
      </h1>

      <div style={{
        backgroundColor: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '16px',
        padding: '32px',
        border: '1px solid rgba(148, 163, 184, 0.2)'
      }}>
        {!editing ? (
          // View Mode
          <div>
            <div style={{ display: 'grid', gap: '24px' }}>
              {/* Username */}
              <div>
                <div style={{ fontSize: '0.875rem', opacity: 0.6, marginBottom: '4px' }}>
                  {t('userProfile.username')}
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: '500' }}>
                  {user.username}
                </div>
              </div>

              {/* Name */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.6, marginBottom: '4px' }}>
                    {t('userProfile.firstName')}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '500' }}>
                    {user.first_name || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.6, marginBottom: '4px' }}>
                    {t('userProfile.lastName')}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '500' }}>
                    {user.last_name || '-'}
                  </div>
                </div>
              </div>

              {/* Email */}
              <div>
                <div style={{ fontSize: '0.875rem', opacity: 0.6, marginBottom: '4px' }}>
                  Email
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: '500' }}>
                  {user.email || '-'}
                </div>
              </div>

              {/* Role */}
              <div>
                <div style={{ fontSize: '0.875rem', opacity: 0.6, marginBottom: '4px' }}>
                  {t('userProfile.role')}
                </div>
                <span style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  backgroundColor: roleColors.bg,
                  color: roleColors.text
                }}>
                  {getRoleName(user.role)}
                </span>
              </div>

              {/* Created At */}
              {user.created_at && (
                <div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.6, marginBottom: '4px' }}>
                    {t('userProfile.createdAt')}
                  </div>
                  <div style={{ fontSize: '1rem' }}>
                    {new Date(user.created_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setEditing(true)}
              style={{
                width: '100%',
                marginTop: '32px',
                background: 'linear-gradient(135deg, #e8b73a, #cc9304)',
                padding: '14px',
                fontSize: '1.125rem',
                fontWeight: '600'
              }}
            >
              ✏️ {t('userProfile.editProfile')}
            </button>
          </div>
        ) : (
          // Edit Mode
          <form onSubmit={handleUpdateProfile}>
            <div style={{ display: 'grid', gap: '20px' }}>
              {/* First Name */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  {t('userProfile.firstName')}
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(206, 212, 218, 0.6)',
                    backgroundColor: 'rgba(30, 41, 59, 0.5)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              {/* Last Name */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  {t('userProfile.lastName')}
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(206, 212, 218, 0.6)',
                    backgroundColor: 'rgba(30, 41, 59, 0.5)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              {/* Email */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(206, 212, 218, 0.6)',
                    backgroundColor: 'rgba(30, 41, 59, 0.5)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{
                padding: '16px',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                marginTop: '8px'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#93c5fd' }}>
                  🔐 {t('userProfile.changePassword')}
                </div>
                <div style={{ fontSize: '0.875rem', opacity: 0.8, marginBottom: '16px' }}>
                  {t('userProfile.passwordHint')}
                </div>

                {/* New Password */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('userProfile.newPassword')}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(206, 212, 218, 0.6)',
                      backgroundColor: 'rgba(30, 41, 59, 0.5)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                {/* Confirm Password */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('userProfile.confirmPassword')}
                  </label>
                  <input
                    type="password"
                    value={formData.password_confirm}
                    onChange={(e) => setFormData({...formData, password_confirm: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(206, 212, 218, 0.6)',
                      backgroundColor: 'rgba(30, 41, 59, 0.5)',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
              <button
                type="submit"
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  padding: '14px',
                  fontSize: '1.125rem',
                  fontWeight: '600'
                }}
              >
                💾 {t('common.save')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    first_name: user.first_name || '',
                    last_name: user.last_name || '',
                    email: user.email || '',
                    password: '',
                    password_confirm: ''
                  });
                }}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #6c757d, #475569)',
                  padding: '14px',
                  fontSize: '1.125rem',
                  fontWeight: '600'
                }}
              >
                ❌ {t('common.cancel')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default UserProfile;
